import React from 'react'
import PropTypes from 'prop-types'

import { reverse } from 'named-urls'
import { useHistory, useLocation } from 'react-router-dom'
import { RHFInput } from 'react-hook-form-input'
import useForm, { FormContext, useFormContext } from 'react-hook-form'

import {
  Paper,
  TextField,
  makeStyles,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  LinearProgress,
} from '@material-ui/core'

import {
  DescriptionOutlined,
  MonetizationOnOutlined,
  FaceOutlined,
  CollectionsBookmarkOutlined,
  AccountCircleOutlined,
  SettingsOutlined,
  AttachMoneyOutlined,
  FindInPageOutlined,
  EmailOutlined,
} from '@material-ui/icons'

import { routes } from 'routes'
import * as service from 'service'
import schemas from './../../../../schemas'
import useFetch from 'hooks/useFetch'

import styles from './styles'

const useStyles = makeStyles(styles)

const FunctionalityGroup = ({
  disabled,
  name,
  controller,
  icon: Icon,
  functionalities: functionalitiesProp,
}) => {
  const classes = useStyles()
  const { register, setValue, watch } = useFormContext()
  const values = watch('functionalities')

  const functionalities = React.useMemo(
    () =>
      functionalitiesProp.filter(
        functionality => functionality.controller === controller,
      ),
    [controller, functionalitiesProp],
  )

  const allChecked = React.useMemo(
    () =>
      !!functionalities.length &&
      functionalities.every(functionality => values[functionality.tag]),
    [values, functionalities],
  )

  const handleChangeCheckAll = event => {
    if (!event.target.checked) {
      functionalities.forEach(functionality =>
        setValue(`functionalities.${functionality.tag}`, false),
      )

      return
    }

    functionalities.forEach(functionality =>
      setValue(`functionalities.${functionality.tag}`, true),
    )
  }

  return (
    <div className={classes.functionalityGroup}>
      <div className={classes.entityTitle}>
        <Icon className={classes.entityIcon} />

        <Typography variant="h5" className={classes.entityName}>
          {name}
        </Typography>
      </div>

      <div className={classes.checkboxContainer}>
        <FormControlLabel
          control={<Checkbox disabled={disabled} />}
          label={`Selecionar todas as opções de ${name}`}
          checked={allChecked}
          onChange={handleChangeCheckAll}
          classes={{ label: classes.checkBoxAllLabel }}
        />

        {functionalities
          .filter(functionality => functionality.controller === controller)
          .map(functionality => (
            <RHFInput
              as={
                <FormControlLabel
                  control={<Checkbox disabled={disabled} />}
                  label={functionality.description}
                  classes={{ label: classes.checkBoxLabel }}
                />
              }
              name={`functionalities.${functionality.tag}`}
              type="checkbox"
              register={register}
              setValue={setValue}
              mode="onChange"
              key={functionality.id}
            />
          ))}
      </div>
    </div>
  )
}

const ProfilesForm = ({
  btnSubmitText,
  isLoading,
  disabled = false,
  data,
  onSubmit,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  const { handleSubmit, register, errors, setValue, watch } = useForm({
    validationSchema: schemas.profile,
    defaultValues: data || {
      functionalities: {},
    },
  })

  const { response: responseFunctionalities } = useFetch(
    service.recurrence.functionalities.get,
    {},
  )

  const functionalities = React.useMemo(
    () =>
      responseFunctionalities
        ? responseFunctionalities.data.functionalities
        : [],
    [responseFunctionalities],
  )

  React.useEffect(() => {
    functionalities.forEach(functionality => {
      if (!data) {
        setValue(`functionalities.${functionality.tag}`, false)
        return
      }

      setValue(
        `functionalities.${functionality.tag}`,
        data.functionalities[functionality.tag] !== undefined
          ? data.functionalities[functionality.tag]
          : true,
      )
    })
  }, [functionalities, setValue, data])

  const handleClickCancel = () => {
    if (!location.state || !location.state.fromUsers) {
      history.push(reverse(routes.settings.all))
      return
    }

    const { user } = location.state

    if (!user.id) {
      history.push({
        pathname: reverse(routes.settings.users.new),
        state: { fromProfiles: true, user },
      })
      return
    }

    history.push({
      pathname: reverse(routes.settings.users.edit, { userId: user.id }),
      state: { fromProfiles: true, user },
    })
  }

  return (
    <Paper className={classes.root}>
      {isLoading && <LinearProgress className={classes.progress} />}

      <FormContext
        register={register}
        setValue={setValue}
        errors={errors}
        watch={watch}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.fields}>
            <RHFInput
              as={
                <TextField
                  label="Nome do perfil"
                  margin="dense"
                  error={!!errors.name}
                  helperText={errors && errors.name && errors.name.message}
                  className={classes.inputDescription}
                  disabled={disabled}
                  fullWidth
                />
              }
              name="name"
              register={register}
              setValue={setValue}
            />
            <RHFInput
              as={
                <TextField
                  label="Descrição breve"
                  margin="dense"
                  error={!!errors.description}
                  helperText={
                    errors && errors.description && errors.description.message
                  }
                  disabled={disabled}
                  fullWidth
                />
              }
              name="description"
              register={register}
              setValue={setValue}
            />
          </div>

          {responseFunctionalities && (
            <>
              <div className={classes.functionalityGroupWrapper}>
                <FunctionalityGroup
                  name="Conta"
                  controller="sellers"
                  icon={AccountCircleOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Configurações"
                  controller="settings"
                  icon={SettingsOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Assinaturas"
                  controller="contracts"
                  icon={DescriptionOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Cobranças adicionais"
                  controller="entries"
                  icon={DescriptionOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Faturas"
                  controller="invoices"
                  icon={MonetizationOnOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Itens de fatura"
                  controller="invoice_items"
                  icon={MonetizationOnOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Notas Fiscais"
                  controller="service_tax_invoices"
                  icon={FindInPageOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Clientes"
                  controller="customers"
                  icon={FaceOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Planos"
                  controller="plans"
                  icon={CollectionsBookmarkOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Impostos"
                  controller="taxes"
                  icon={AttachMoneyOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />

                <FunctionalityGroup
                  name="Emails"
                  controller="emails"
                  icon={EmailOutlined}
                  functionalities={functionalities}
                  disabled={disabled}
                />
              </div>

              {!disabled && (
                <Typography className={classes.helperText}>
                  <strong>Atenção:</strong> qualquer alteração feita nas
                  permissões será aplicada para todos os usuários deste perfil.
                </Typography>
              )}

              <div className={classes.buttonsWrapper}>
                {disabled ? (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleClickCancel}
                  >
                    Voltar
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Carregando...' : btnSubmitText}
                    </Button>

                    <Button
                      color="primary"
                      variant="outlined"
                      disabled={isLoading}
                      onClick={handleClickCancel}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </form>
      </FormContext>
    </Paper>
  )
}

ProfilesForm.propTypes = {
  btnSubmitText: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
}

export default ProfilesForm
